

import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { OptionsTextfield } from './options-textfield';
import { EntitySettingsForm, FieldDefinition } from './types';

import { useResponsiveValue } from '@/hooks/use-responsive-value';

interface SectionFieldsProps {
  sectionIndex: number;
}

export function SectionFields({ sectionIndex }: SectionFieldsProps) {
  const { t } = useTranslation();
  const methods = useFormContext<EntitySettingsForm>();
  const resolveResponsive = useResponsiveValue();
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: `advancedSections.${sectionIndex}.fields`,
  });

  // Validate field label for invalid path characters
  const validateFieldLabel = (label: string): string | true => {
    const invalidChars = ['~', '*', '/', '[', ']'];
    const foundInvalidChars = invalidChars.filter(char => label.includes(char));

    if (foundInvalidChars.length > 0) {
      return t('entity_settings.invalid_field_chars', { chars: foundInvalidChars.join(', ') });
    }

    return true;
  };

  return (
    <Box sx={{ pl: 4 }}>
      <Stack spacing={2}>
        {fields.map((field, fieldIndex) => {
          const fieldType = methods.watch(`advancedSections.${sectionIndex}.fields.${fieldIndex}.type`);
          const fieldLabel = methods.watch(`advancedSections.${sectionIndex}.fields.${fieldIndex}.label`) || '';
          const labelError = validateFieldLabel(fieldLabel);
          const hasError = labelError !== true;

          return (
            <Box key={field.id}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid size={resolveResponsive({ xs: 6, md: 3 })}>
                  <TextField
                    {...methods.register(`advancedSections.${sectionIndex}.fields.${fieldIndex}.label`)}
                    label={t('entity_settings.field_label')}
                    fullWidth
                    variant="filled"
                    size="small"
                    error={hasError}
                    helperText={hasError ? labelError : undefined}
                    InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5 } }}
                  />
                </Grid>
                <Grid size={resolveResponsive({ xs: 6, md: 3 })}>
                  <Controller
                    name={`advancedSections.${sectionIndex}.fields.${fieldIndex}.type`}
                    control={methods.control}
                    defaultValue="text"
                    render={({ field }) => (
                      <FormControl fullWidth variant="filled" size="small">
                        <InputLabel shrink>{t('entity_settings.type')}</InputLabel>
                        <Select
                          {...field}
                          label={t('entity_settings.type')}
                          sx={{ borderRadius: 1.5, '&:before, &:after': { display: 'none' } }}
                        >
                          <MenuItem value="text">{t('entity_settings.short_text')}</MenuItem>
                          <MenuItem value="textarea">{t('entity_settings.long_text')}</MenuItem>
                          <MenuItem value="number">{t('entity_settings.number')}</MenuItem>
                          <MenuItem value="select">{t('entity_settings.selection')}</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid size={resolveResponsive({ xs: 6, md: 4 })}>
                  <TextField
                    {...methods.register(`advancedSections.${sectionIndex}.fields.${fieldIndex}.placeholder`)}
                    label={t('entity_settings.placeholder')}
                    fullWidth
                    variant="filled"
                    size="small"
                    InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5 } }}
                  />
                </Grid>
                <Grid size={resolveResponsive({ xs: 6, md: 1 })} sx={{ textAlign: 'right', mt: 0.5 }}>
                  <IconButton onClick={() => remove(fieldIndex)} color="error" size="small">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>

                {fieldType === 'select' && (
                  <Grid size={resolveResponsive({ xs: 12 })}>
                    <Box sx={{ ml: 2, mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 700, textTransform: 'uppercase' }}>
                        {t('entity_settings.selection_options')}
                      </Typography>
                      <OptionsTextfield sectionIndex={sectionIndex} fieldIndex={fieldIndex} />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          );
        })}

        <Button
          size="small"
          variant="text"
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => append({ id: crypto.randomUUID(), label: '', type: 'text', placeholder: '', order: fields.length, options: [] } as FieldDefinition)}
          sx={{ alignSelf: 'flex-start', color: 'text.secondary', fontSize: '0.7rem' }}
        >
          {t('entity_settings.add_field')}
        </Button>
      </Stack>
    </Box>
  );
}
